import { Component, ChangeDetectionStrategy, input, signal, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationService } from '@outlaw/core';
import { CalendarWidget, LinkWidget } from '@outlaw/community';

const STATIC_WIDGETS = {
  CALENDAR: (calendar: CalendarWidget): LinkWidget => ({
    title: calendar?.title || 'Tour Schedule',
    linkUrl: '/widgets/calendar',
    iconClass: 'icon-widget-tour',
  }),
  LATEST_VIDEOS: (): LinkWidget => ({
    title: 'Latest Videos',
    linkUrl: '/widgets/latest-videos',
    iconClass: 'icon-widget-video',
  }),
};

@Component({
  selector: 'app-widget-menu',
  templateUrl: './widget-menu.component.html',
  styleUrls: ['./widget-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetMenuComponent {
  readonly widgets = input<LinkWidget[]>([]);
  readonly calendar = input<CalendarWidget | null>(null);

  readonly widgetMenuIsOpen = signal(false);

  readonly #router = inject(Router);
  readonly #navigationService = inject(NavigationService);

  readonly hasCalendarEvents = computed(() => {
    const calendar = this.calendar();
    return !!calendar?.events?.length;
  });

  readonly processedWidgets = computed(() => {
    const dynamicWidgets = this.widgets() || [];
    const staticWidgets: LinkWidget[] = [];

    if (this.hasCalendarEvents()) {
      staticWidgets.push(STATIC_WIDGETS.CALENDAR(this.calendar()));
    }

    staticWidgets.push(STATIC_WIDGETS.LATEST_VIDEOS());

    return [...staticWidgets, ...dynamicWidgets];
  });

  toggleWidgetMenu(): void {
    this.widgetMenuIsOpen.update((state) => !state);
  }

  handleWidgetClick(widget: LinkWidget): void {
    if (!widget.linkUrl) {
      return;
    }

    this.isExternalLink(widget.linkUrl)
      ? this.openExternalLink(widget.linkUrl)
      : this.navigateToRoute(widget.linkUrl);

    this.widgetMenuIsOpen.set(false);
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private isExternalLink(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  private openExternalLink(url: string): void {
    this.#navigationService.openOnExternalPage(url);
  }

  private navigateToRoute(route: string): void {
    this.#router.navigate([route]);
  }
}
